import type { No } from '@mymaxx/flow-engine';
import type { FluxoResumo, SetorResumo } from '@mymaxx/shared';
import { Botao, CampoTexto } from '@mymaxx/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CATALOGO_NOS } from './catalogo-nos';

/**
 * Painel lateral com as propriedades do nó selecionado. Cada tipo mostra só o que lhe cabe;
 * alterações sobem para o editor, que mantém o grafo em estado único.
 */
export function PropriedadesNo({
  no,
  setores,
  fluxos,
  onChange,
  onExcluir,
}: {
  no: No;
  setores: SetorResumo[];
  fluxos: FluxoResumo[];
  onChange: (no: No) => void;
  onExcluir: () => void;
}) {
  const descricao = CATALOGO_NOS[no.tipo];
  /** Atalho para atualizar um campo mantendo o tipo do nó. */
  const set = (campos: Partial<No>) => onChange({ ...no, ...campos } as No);

  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-neutral-200 dark:border-neutral-800">
      <header className="border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <p className="flex items-center gap-2 text-sm font-medium">
          <descricao.Icone size={15} style={{ color: descricao.cor }} />
          {descricao.nome}
        </p>
        <p className="mt-0.5 text-xs text-neutral-500">{descricao.descricao}</p>
      </header>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {no.tipo === 'inicio' && (
          <p className="text-sm text-neutral-500">
            Todo fluxo tem um único início. Ligue-o ao primeiro passo da conversa.
          </p>
        )}

        {no.tipo === 'mensagem' && (
          <CampoArea
            rotulo="Mensagem"
            valor={no.texto}
            onChange={(texto) => set({ texto })}
            ajuda="Use {{variavel}} para inserir algo que o cliente respondeu."
          />
        )}

        {no.tipo === 'pergunta' && (
          <>
            <CampoArea rotulo="Pergunta" valor={no.texto} onChange={(texto) => set({ texto })} />
            <CampoTexto
              rotulo="Guardar resposta em"
              value={no.variavel}
              onChange={(e) => set({ variavel: e.target.value })}
              ajuda="Nome da variável, sem chaves. Ex.: documento"
            />
            <Seletor
              rotulo="Formato esperado"
              valor={no.validacao}
              opcoes={[
                { valor: 'texto', rotulo: 'Texto livre' },
                { valor: 'documento', rotulo: 'CPF ou CNPJ' },
                { valor: 'numero', rotulo: 'Número' },
              ]}
              onChange={(validacao) => set({ validacao: validacao as 'texto' })}
            />
            <CampoTexto
              rotulo="Mensagem se responder errado"
              value={no.mensagemInvalida ?? ''}
              onChange={(e) => set({ mensagemInvalida: e.target.value })}
            />
            <CampoTexto
              rotulo="Desistir depois de quantos erros"
              type="number"
              min={1}
              max={10}
              value={no.maxTentativas ?? ''}
              onChange={(e) =>
                set({ maxTentativas: e.target.value ? Number(e.target.value) : undefined })
              }
              ajuda='Em branco, pergunta para sempre. Com um número, aparece a saída "Não conseguiu responder" para você ligar a um setor.'
            />

            {/*
              Palavras-chave que pulam a pergunta. Cada atalho vira uma saída própria do
              nó no canvas — é assim que o técnico digita "!liberacao" no lugar do CPF.
            */}
            <div className="space-y-3">
              <div>
                <p className="text-sm font-medium">Atalhos por palavra-chave</p>
                <p className="text-xs text-neutral-500">
                  Se o cliente digitar uma destas palavras, a pergunta é pulada e o fluxo segue pela
                  saída do atalho. Acento, maiúscula e espaço não importam: quem cadastra "!tecnico"
                  já atende quem digita "! Técnico". Separe as palavras por vírgula.
                </p>
              </div>
              {no.atalhos.map((atalho, i) => (
                <div
                  key={i}
                  className="space-y-2 rounded-lg border border-neutral-200 p-2 dark:border-neutral-700"
                >
                  <div className="flex items-center gap-2">
                    <input
                      value={atalho.rotulo}
                      aria-label={`Nome do atalho ${i + 1}`}
                      placeholder="Ex.: Técnico em campo"
                      onChange={(e) =>
                        set({
                          atalhos: no.atalhos.map((a, j) =>
                            j === i ? { ...a, rotulo: e.target.value } : a,
                          ),
                        })
                      }
                      className="flex-1 rounded border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                    />
                    <button
                      type="button"
                      aria-label={`Remover atalho ${i + 1}`}
                      onClick={() => set({ atalhos: no.atalhos.filter((_, j) => j !== i) })}
                      className="text-neutral-400 hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <CampoPalavras
                    key={`${no.id}-${i}`}
                    palavras={atalho.palavras}
                    rotulo={`Palavras do atalho ${i + 1}`}
                    onMudar={(palavras) =>
                      set({
                        atalhos: no.atalhos.map((a, j) => (j === i ? { ...a, palavras } : a)),
                      })
                    }
                  />
                </div>
              ))}
              <Botao
                variante="secundario"
                onClick={() =>
                  set({
                    atalhos: [
                      ...no.atalhos,
                      { id: `atalho${no.atalhos.length + 1}`, rotulo: '', palavras: [] },
                    ],
                  })
                }
              >
                <Plus size={14} /> Adicionar atalho
              </Botao>
            </div>
          </>
        )}

        {no.tipo === 'menu' && (
          <>
            <CampoArea rotulo="Pergunta" valor={no.texto} onChange={(texto) => set({ texto })} />
            <div className="space-y-3">
              <p className="text-sm font-medium">Opções</p>
              {no.opcoes.map((opcao, i) => (
                <div
                  key={i}
                  className="space-y-2 rounded-lg border border-neutral-200 p-2 dark:border-neutral-700"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-neutral-500">{i + 1}.</span>
                    <input
                      value={opcao.rotulo}
                      aria-label={`Texto da opção ${i + 1}`}
                      onChange={(e) =>
                        set({
                          opcoes: no.opcoes.map((o, j) =>
                            j === i ? { ...o, rotulo: e.target.value } : o,
                          ),
                        })
                      }
                      className="flex-1 rounded border border-neutral-300 px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
                    />
                    <button
                      type="button"
                      aria-label={`Remover opção ${i + 1}`}
                      onClick={() => set({ opcoes: no.opcoes.filter((_, j) => j !== i) })}
                      className="text-neutral-400 hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  {/* Mesmo campo, mesmo motivo: digitar "segunda via" num sinônimo era
                      impossível enquanto o texto se refazia a cada tecla. */}
                  <CampoPalavras
                    key={`${no.id}-opcao-${i}`}
                    palavras={opcao.sinonimos}
                    rotulo={`Sinônimos da opção ${i + 1}`}
                    placeholder="Sinônimos: boleto, fatura, segunda via"
                    onMudar={(sinonimos) =>
                      set({
                        opcoes: no.opcoes.map((o, j) => (j === i ? { ...o, sinonimos } : o)),
                      })
                    }
                  />
                  {/* Opção que não é para todo mundo. Em branco, aparece sempre — que é o
                      caso de quase todas. */}
                  <label className="block text-[11px] text-neutral-500">
                    Mostrar só quando
                    <input
                      value={opcao.visivelSe ?? ''}
                      aria-label={`Condição da opção ${i + 1}`}
                      placeholder="cliente.ehTecnico · cliente.tipoTecnico=rede · vazio = sempre"
                      onChange={(e) =>
                        set({
                          opcoes: no.opcoes.map((o, j) =>
                            j === i ? { ...o, visivelSe: e.target.value.trim() || undefined } : o,
                          ),
                        })
                      }
                      className="mt-0.5 w-full rounded border border-neutral-300 px-2 py-1 font-mono text-xs dark:border-neutral-700 dark:bg-neutral-900"
                    />
                  </label>
                </div>
              ))}
              <Botao
                variante="secundario"
                onClick={() =>
                  set({
                    opcoes: [
                      ...no.opcoes,
                      {
                        id: `opcao${no.opcoes.length + 1}_${Date.now().toString(36)}`,
                        rotulo: 'Nova opção',
                        sinonimos: [],
                      },
                    ],
                  })
                }
              >
                <Plus size={14} /> Adicionar opção
              </Botao>
              <p className="text-xs text-neutral-500">
                O cliente pode responder com o número ou com um dos sinônimos.
              </p>
            </div>
          </>
        )}

        {no.tipo === 'condicao' && (
          <>
            <CampoTexto
              rotulo="Variável"
              value={no.variavel}
              onChange={(e) => set({ variavel: e.target.value })}
              ajuda="Ex.: cliente.totalPontos"
            />
            <Seletor
              rotulo="Comparação"
              valor={no.operador}
              opcoes={[
                { valor: 'igual', rotulo: 'é igual a' },
                { valor: 'diferente', rotulo: 'é diferente de' },
                { valor: 'contem', rotulo: 'contém' },
                { valor: 'existe', rotulo: 'está preenchida' },
                { valor: 'maior', rotulo: 'é maior que' },
                { valor: 'menor', rotulo: 'é menor que' },
              ]}
              onChange={(operador) => set({ operador: operador as 'igual' })}
            />
            {no.operador !== 'existe' && (
              <CampoTexto
                rotulo="Valor"
                value={no.valor ?? ''}
                onChange={(e) => set({ valor: e.target.value })}
              />
            )}
          </>
        )}

        {no.tipo === 'escolher_ponto' && (
          <>
            <CampoTexto
              rotulo="Pergunta"
              value={no.texto}
              onChange={(e) => set({ texto: e.target.value })}
            />
            <CampoTexto
              rotulo="Variável com a lista de pontos"
              value={no.variavel}
              onChange={(e) => set({ variavel: e.target.value })}
              ajuda="Padrão: cliente.pontos — é onde a consulta ao ERP e o cliente logado guardam."
            />
            <CampoTexto
              rotulo="Quando não entender a resposta"
              value={no.mensagemInvalida ?? ''}
              onChange={(e) => set({ mensagemInvalida: e.target.value })}
              ajuda="Opcional. A lista é repetida junto com este aviso."
            />
            <p className="rounded-lg bg-neutral-100 p-2 text-xs text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300">
              Com um ponto só, o nó não pergunta nada e segue direto — perguntar o óbvio é ruído.
              Sem nenhum ponto, sai pela saída <strong>Sem ponto</strong>.
            </p>
          </>
        )}

        {no.tipo === 'acao_hubsoft' && (
          <>
            <Seletor
              rotulo="O que consultar"
              valor={no.acao}
              opcoes={[
                { valor: 'buscar_pontos_por_documento', rotulo: 'Cliente e pontos por CPF/CNPJ' },
                { valor: 'status_servico', rotulo: 'Situação do serviço' },
                { valor: 'faturas_abertas', rotulo: 'Faturas em aberto (indisponível)' },
              ]}
              onChange={(acao) => set({ acao: acao as 'status_servico' })}
            />
            {/* Não é "em breve": não há data, e depende de o ERP liberar a consulta. Quem
                escolher isto precisa saber o que acontece no ar, senão monta um fluxo que
                erra sempre e parece defeito do bot. */}
            {no.acao === 'faturas_abertas' && (
              <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-200">
                Esta consulta ainda não foi liberada no ERP. Enquanto isso, o nó sempre sai pelo
                caminho de <strong>não encontrado</strong> — não use num fluxo publicado.
              </p>
            )}
            <CampoTexto
              rotulo="Guardar resultado em"
              value={no.variavelResultado}
              onChange={(e) => set({ variavelResultado: e.target.value })}
            />
            <div>
              <p className="mb-1 text-sm font-medium">Parâmetros</p>
              {Object.entries(no.parametros).map(([chave, valor]) => (
                <div key={chave} className="mb-1 flex items-center gap-2">
                  <span className="w-24 shrink-0 text-xs text-neutral-500">{chave}</span>
                  <input
                    value={valor}
                    aria-label={`Valor de ${chave}`}
                    onChange={(e) =>
                      set({ parametros: { ...no.parametros, [chave]: e.target.value } })
                    }
                    className="flex-1 rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
                  />
                </div>
              ))}
              <p className="mt-1 text-xs text-neutral-500">
                Use {'{{variavel}}'} para aproveitar o que o cliente já respondeu.
              </p>
            </div>
          </>
        )}

        {(no.tipo === 'horario' || no.tipo === 'transferir_setor') && (
          <Seletor
            rotulo="Setor"
            valor={no.setorId}
            opcoes={[
              { valor: '', rotulo: 'Selecione…' },
              ...setores.map((s) => ({ valor: s.id, rotulo: s.nome })),
            ]}
            onChange={(setorId) => set({ setorId })}
          />
        )}

        {no.tipo === 'transferir_fluxo' && (
          <Seletor
            rotulo="Fluxo de destino"
            valor={no.fluxoId}
            opcoes={[
              { valor: '', rotulo: 'Selecione…' },
              ...fluxos.map((f) => ({ valor: f.id, rotulo: f.nome })),
            ]}
            onChange={(fluxoId) => set({ fluxoId })}
          />
        )}

        {no.tipo === 'definir_variavel' && (
          <>
            <CampoTexto
              rotulo="Variável"
              value={no.variavel}
              onChange={(e) => set({ variavel: e.target.value })}
            />
            <CampoTexto
              rotulo="Valor"
              value={no.valor}
              onChange={(e) => set({ valor: e.target.value })}
              ajuda="Aceita {{outras_variaveis}}."
            />
          </>
        )}

        {no.tipo === 'aguardar' && (
          <CampoTexto
            rotulo="Esperar (segundos)"
            type="number"
            min={1}
            value={no.segundos}
            onChange={(e) => set({ segundos: Number(e.target.value) })}
          />
        )}

        {no.tipo === 'webhook_http' && (
          <>
            <CampoTexto
              rotulo="URL"
              value={no.url}
              onChange={(e) => set({ url: e.target.value })}
            />
            <Seletor
              rotulo="Método"
              valor={no.metodo}
              opcoes={[
                { valor: 'POST', rotulo: 'POST' },
                { valor: 'GET', rotulo: 'GET' },
              ]}
              onChange={(metodo) => set({ metodo: metodo as 'POST' })}
            />
            <CampoTexto
              rotulo="Guardar resposta em"
              value={no.variavelResultado}
              onChange={(e) => set({ variavelResultado: e.target.value })}
            />
          </>
        )}

        {no.tipo === 'encerrar' && (
          <CampoArea
            rotulo="Mensagem de despedida"
            valor={no.mensagem ?? ''}
            onChange={(mensagem) => set({ mensagem })}
            ajuda="Deixe vazio para encerrar sem mensagem."
          />
        )}
      </div>

      {no.tipo !== 'inicio' && (
        <footer className="border-t border-neutral-200 p-4 dark:border-neutral-800">
          <Botao variante="perigo" className="w-full" onClick={onExcluir}>
            <Trash2 size={14} /> Excluir nó
          </Botao>
        </footer>
      )}
    </aside>
  );
}

function CampoArea({
  rotulo,
  valor,
  onChange,
  ajuda,
}: {
  rotulo: string;
  valor: string;
  onChange: (v: string) => void;
  ajuda?: string;
}) {
  return (
    <label className="block text-sm font-medium">
      {rotulo}
      <textarea
        rows={3}
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
      />
      {ajuda && <span className="block text-xs font-normal text-neutral-500">{ajuda}</span>}
    </label>
  );
}

function Seletor({
  rotulo,
  valor,
  opcoes,
  onChange,
}: {
  rotulo: string;
  valor: string;
  opcoes: { valor: string; rotulo: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="block text-sm font-medium">
      {rotulo}
      <select
        value={valor}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm dark:border-neutral-700 dark:bg-neutral-900"
      >
        {opcoes.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.rotulo}
          </option>
        ))}
      </select>
    </label>
  );
}

/**
 * As palavras-chave de um atalho, separadas por vírgula.
 *
 * Guarda o texto cru enquanto se digita, e é esse o ponto. Antes o campo era desenhado a
 * partir da lista já partida (`palavras.join(', ')`) e refeito a cada tecla: a vírgula
 * recém-digitada sumia junto com o espaço depois dela, porque não havia palavra nenhuma
 * entre elas ainda. Resultado prático — não dava para escrever "! tecnico", que é como
 * metade dos técnicos digita (relato de 01/10).
 *
 * A lista sobe a cada tecla, para o fluxo nunca ficar com o texto e o dado desencontrados;
 * ao sair do campo, o texto se arruma na forma canônica.
 */
function CampoPalavras({
  palavras,
  rotulo,
  placeholder = '!tecnico, ! tecnico, !liberacao',
  onMudar,
}: {
  palavras: string[];
  rotulo: string;
  placeholder?: string;
  onMudar: (palavras: string[]) => void;
}) {
  const campo = useRef<HTMLInputElement>(null);
  const canonico = palavras.join(', ');
  const [texto, setTexto] = useState(canonico);

  // Enquanto o campo está com o foco, o que vale é o que a pessoa está escrevendo — este
  // efeito não encosta nele. Fora disso ele acompanha a lista: é o que mantém o campo certo
  // quando outro atalho é removido e os de baixo sobem uma posição.
  useEffect(() => {
    if (document.activeElement !== campo.current) setTexto(canonico);
  }, [canonico]);

  const partir = (valor: string) =>
    valor
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);

  return (
    <input
      ref={campo}
      value={texto}
      aria-label={rotulo}
      placeholder={placeholder}
      onChange={(e) => {
        setTexto(e.target.value);
        onMudar(partir(e.target.value));
      }}
      onBlur={() => setTexto(partir(texto).join(', '))}
      className="w-full rounded border border-neutral-300 px-2 py-1 text-xs dark:border-neutral-700 dark:bg-neutral-900"
    />
  );
}
